import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  customerConfig,
  getSharedLoyaltyState,
  LoyaltySsoService,
  State as LoyaltyConfigState
} from '@uxdf/direct-shared-lib';
import { combineLatest, Observable, Subject } from 'rxjs';
import { State as CustomerState } from '../../../../redux/reducers/customer';
import { State as LoyaltyTileState } from '../../../../redux/reducers/reducers-loyalty-tile';
import { LoyaltyTileHelperService } from '../../../../redux/services/loyalty-tile-helper.service';
import * as LoyaltyTileActions from '../../../../redux/actions/actions-loyalty-tile';
import {
  getAccountState,
  getBonusPointsState,
  getLoyaltyTileState,
  getCustomerProfileState,
  getAgentState
} from '../../../../redux/reducers';
import { AccountState } from '../../../../redux/models/loyalty/account.state';
import { filter, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { BonusPointsState } from '../../../../redux/models/loyalty/bonus-points.state';
import { BackendLoyaltyService } from '../../../../redux/services/backend-loyalty.service';
import { CustomerState as CustomerProfileState } from '../../../../redux/models/loyalty/customer.state';
import * as BonusPointsAction from '../../../../redux/actions/loyalty/actions-bonus-points';
import * as CustomerAction from '../../../../redux/actions/loyalty/actions-customer';
import * as AgentAction from '../../../../redux/actions/loyalty/actions-agent';
import { AgentState } from '../../../../redux/models/loyalty/agent.state';
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'app-loyalty-tile',
  templateUrl: './loyalty-tile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoyaltyTileComponent implements OnInit, OnDestroy {
  @Input() type = 's';

  public configs$: Observable<{
    customerConfig: CustomerState;
    sharedLoyaltyState: LoyaltyConfigState;
    loyaltyTileConfig: LoyaltyTileState;
    accountState: AccountState;
    customerProfileState: CustomerProfileState;
  }>;

  public agentState$: Observable<AgentState>;
  public pointsState$: Observable<BonusPointsState>;

  public destroy$ = new Subject();
  public createAccountClicked$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly store: Store<any>,
    private readonly loyaltyTileHelperService: LoyaltyTileHelperService,
    private readonly cd: ChangeDetectorRef,
    private readonly backendLoyaltyService: BackendLoyaltyService,
    private readonly router: Router,
    private readonly loyaltySsoService: LoyaltySsoService
  ) {}

  ngOnInit() {
    this.pointsState$ = this.store.pipe(select(getBonusPointsState));
    this.agentState$ = this.store.pipe(select(getAgentState));

    this.configs$ = combineLatest([
      this.store.pipe(select(customerConfig)),
      this.store.pipe(select(getSharedLoyaltyState)),
      this.store.pipe(select(getLoyaltyTileState)),
      this.store.pipe(select(getAccountState)),
      this.store.pipe(select(getCustomerProfileState))]
    ).pipe(
      map(([customer, sharedLoyaltyState, loyaltyTileConfig, accountState, customerProfileState]) => ({
        customerConfig: customer,
        sharedLoyaltyState,
        loyaltyTileConfig,
        accountState,
        customerProfileState
      })),
      tap(() => this.cd.detectChanges())
    );

    this.store.dispatch(
      LoyaltyTileActions.loyaltyTileInit({
        isRequested: this.loyaltyTileHelperService.isRequestSent()
      })
    );

    this.agentState$
      .pipe(
        withLatestFrom(this.createAccountClicked$),
        filter(([state, createAccountClicked]) => !state.isLoading && state.data.number !== 0 && createAccountClicked),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.router.navigate(['/amadeus-plus-me/enrollment'], {
          queryParams: {
            displayView: 'finalForm'
          }
        });
      });
  }

  onRequest() {
    this.store.dispatch(LoyaltyTileActions.loyaltyTileRequest());
  }

  onApprove() {
    this.store.dispatch(LoyaltyTileActions.loyaltyTileApprove());
  }

  onLoadPoints() {
    this.store.dispatch(new BonusPointsAction.GetBonusPoints());
  }

  onLogin() {
    this.router.navigate(['/amadeus-plus-me/enrollment'], {
      queryParams: {
        displayView: 'loginForm'
      }
    });
  }

  onCreateAccount() {
    this.createAccountClicked$.next(true);
    this.store.dispatch(new AgentAction.RegisterAgent());
  }

  getCustomerProfile(storeCode: string) {
    this.store.dispatch(new CustomerAction.GetCustomer({ storeCode }));
  }

  redirectToOrderGifts() {
    this.loyaltySsoService.openLink('amadeus-plus-me-gift-catalog');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.createAccountClicked$.complete();
  }
}
